/**
 * 모션·스크롤 통합 설정
 * --------------------------------------------------------------------
 * 모든 GSAP 핀/스크럽 인터랙션과 Lenis 스무스 스크롤이 이 파일의 값을 사용합니다.
 *
 * - "desktop" / "mobile" 두 값이 있는 항목은 화면 너비 768px 기준으로 자동 분기됩니다.
 * - 더 부드럽게 → scrub ↑ , distanceMultiplier ↑
 * - 더 빠르게 → scrub ↓ , distanceMultiplier ↓
 *
 * 외부(예: index.php)에서도 덮어쓸 수 있도록 window.MAIN_MOTION_CONFIG 가 우선 적용됩니다.
 */

const MOBILE_BREAKPOINT = 768;

const DEFAULTS = {
  /**
   * Lenis 기반 스무스 스크롤
   * - mobile.syncTouch: true → 빠르게 터치/플릭 해도 네이티브 관성이 인터랙션을 휙 지나가지 않도록 Lenis가 흡수
   * - touchInertiaMultiplier ↑ → 손가락 떼고 밀려나가는 거리 ↑
   */
  lenis: {
    desktop: {
      duration: 1.05,
      lerp: 0.1,
      smoothWheel: true,
      wheelMultiplier: 0.7,
      touchMultiplier: 1,
      syncTouch: false,
    },
    mobile: {
      duration: 1.25,
      lerp: 0.09,
      smoothWheel: true,
      wheelMultiplier: 0.9,
      touchMultiplier: 1.35,
      syncTouch: true,
      syncTouchLerp: 0.075,
      touchInertiaMultiplier: 24,
    },
  },

  /**
   * #system 카드 두 장(Mycelium / PCLM) 스택 인터랙션
   * - scrub: 스크롤 따라잡는 시간(초). 클수록 부드럽고, 강한 입력에도 덜 튄다.
   * - distanceMultiplier: 핀 구간 길이 배율(기존 ×1.16 기준)
   */
  systemCards: {
    scrub: { desktop: 0.7, mobile: 1.45 },
    distanceMultiplier: { desktop: 1.16, mobile: 1.45 },
  },

  /**
   * #system 슬라이드 핀(LSSC / CELMURE / PCLM)
   * - scrub: 슬라이드 전환 부드러움
   * - holdViewport: 첫 슬라이드 머무는 길이 (×100vh)
   * - perSlideViewport: 슬라이드 한 장 추가될 때마다 늘어나는 길이 (×100vh)
   */
  materialPin: {
    scrub: { desktop: 0.92, mobile: 1.6 },
    holdViewport: { desktop: 2.42, mobile: 2.7 },
    perSlideViewport: { desktop: 0.52, mobile: 0.78 },
  },

  /**
   * #value Dual System → One Continuous Loop 다이어그램
   * - scrub: 단계 전환 부드러움
   * - distanceMultiplier: 핀 전체 스크롤 길이 배율
   */
  valueDiagram: {
    scrub: { desktop: 1.05, mobile: 1.7 },
    distanceMultiplier: { desktop: 1.22, mobile: 1.5 },
  },
};

function deepMerge(base, override) {
  if (!override || typeof override !== "object") return base;
  const out = Array.isArray(base) ? base.slice() : { ...base };
  Object.keys(override).forEach((key) => {
    const a = base?.[key];
    const b = override[key];
    if (
      a &&
      typeof a === "object" &&
      !Array.isArray(a) &&
      b &&
      typeof b === "object" &&
      !Array.isArray(b)
    ) {
      out[key] = deepMerge(a, b);
    } else {
      out[key] = b;
    }
  });
  return out;
}

const userOverrides =
  typeof window !== "undefined" && window.MAIN_MOTION_CONFIG
    ? window.MAIN_MOTION_CONFIG
    : null;

export const MOTION_CONFIG = deepMerge(DEFAULTS, userOverrides);

if (typeof window !== "undefined") {
  window.MAIN_MOTION_CONFIG = MOTION_CONFIG;
}

/** 현재 화면 너비가 모바일 분기인지 */
export function isMobileViewport() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  );
}

/** { desktop, mobile } 형태의 값에서 현재 분기 값을 꺼낸다 */
export function pickMotion(pair, fallback) {
  if (pair && typeof pair === "object" && "desktop" in pair) {
    return isMobileViewport() ? pair.mobile : pair.desktop;
  }
  return fallback ?? pair;
}
